import { Injectable } from '@angular/core';
import { Observable, combineLatest, of, forkJoin } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { StatusManageService, DashboardStats } from './status-manage.service';
import { StatisticsService, CompanyStatisticsResponse } from './statistics.service';

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    fill?: boolean;
    tension?: number;
  }[];
}

// DashboardStats agora está importado do StatusManageService

export interface ActivityItem {
  id: string;
  type: 'checkin' | 'guard_assigned' | 'device_request' | 'position_created';
  description: string;
  timestamp: Date;
  icon: string;
  color: string;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(
    private statusManageService: StatusManageService,
    private statisticsService: StatisticsService,
  ) {}

  getDashboardStats(): Observable<DashboardStats> {
    return this.statisticsService.getStatistics().pipe(
      map((stats: CompanyStatisticsResponse) => this.mapStatisticsToDashboardStats(stats)),
      catchError((error) => {
        console.error('Erro ao buscar estatísticas:', error);
        // Fallback para o método antigo em caso de erro
        return this.statusManageService.getDashboardStats();
      }),
    );
  }

  private mapStatisticsToDashboardStats(stats: CompanyStatisticsResponse): DashboardStats {
    return {
      totalGuards: stats.guards?.total || 0,
      activeGuards: stats.guards?.active || 0,
      inactiveGuards: stats.guards?.inactive || 0,
      totalPositions: stats.positions?.total || 0,
      activePositions: stats.positions?.active || 0,
      inactivePositions: stats.positions?.inactive || 0,
      totalUsers: stats.users?.total || 0,
      activeUsers: stats.users?.total || 0, // Assumindo que todos os usuários estão ativos
      totalDevices: stats.devices?.total || 0,
      activeDevices: stats.devices?.active || 0,
      todayCheckIns: stats.checkins?.today || 0,
      last7DaysCheckIns: stats.checkins?.last7Days || 0,
      pendingRequests: stats.requests?.pending || 0,
      totalAccessRequests: stats.requests?.total || 0, // Não está na resposta da API
      pendingAccessRequests: stats.requests?.pending || 0, // Não está na resposta da API
      totalSupervisors: stats.supervisors?.total || 0,
      activeSchedules: stats.supervisors?.activeSchedules || 0,
      totalPatrolRoutes: stats.patrolsStats?.total_routes || 0,
      activePatrolRoutes: stats.patrolsStats?.active_routes || 0,
      totalCheckpoints: stats.patrolsStats?.total_checkpoints || 0,
      patrolsCompletedToday: stats.patrols?.completedToday || 0,
      patrolsInProgress: stats.patrols?.inProgress || 0,
      patrolsToday: stats.patrols?.today || 0,
      complianceRate: stats.patrolsStats?.compliance_rate || 0,
      positionsWithoutCheckIn24h: stats.positionsCheckinsTable?.positionsWithoutCheckIn24h || 0,
    };
  }

  getCheckInsChartData(days: number = 7): Observable<ChartData> {
    return this.statusManageService.getCheckins().pipe(
      map((checkIns) => {
        const today = new Date();
        const labels: string[] = [];
        const data: number[] = [];

        for (let i = days - 1; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          date.setHours(0, 0, 0, 0);

          const dayCheckIns = checkIns.filter((checkin: any) => {
            const checkinDate = new Date(checkin.created_at);
            checkinDate.setHours(0, 0, 0, 0);
            return checkinDate.getTime() === date.getTime();
          }).length;

          labels.push(date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit' }));
          data.push(dayCheckIns);
        }

        return {
          labels,
          datasets: [
            {
              label: 'Check-Presence',
              data,
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderColor: 'rgb(59, 130, 246)',
              tension: 0.4,
              fill: true,
            },
          ],
        };
      }),
      catchError((error) => {
        console.error('Erro ao carregar dados de Check-Presence:', error);
        return of(this.getMockCheckInsData());
      }),
    );
  }

  getDeviceStatusChartData(): Observable<ChartData> {
    return this.statusManageService.getDevices().pipe(
      map((devices) => {
        const statusCounts = this.countDevicesByStatus(devices);

        return {
          labels: Object.keys(statusCounts),
          datasets: [
            {
              label: 'Dispositivos',
              data: Object.values(statusCounts),
              backgroundColor: [
                'rgba(34, 197, 94, 0.8)', // activo - Verde
                'rgba(239, 68, 68, 0.8)', // inactivo - Vermelho
                'rgba(249, 115, 22, 0.8)', // Manutenção - Laranja
                'rgba(156, 163, 175, 0.8)', // Desconhecido - Cinza
              ],
              borderColor: [
                'rgb(34, 197, 94)',
                'rgb(239, 68, 68)',
                'rgb(249, 115, 22)',
                'rgb(156, 163, 175)',
              ],
              borderWidth: 2,
            },
          ],
        };
      }),
      catchError((error) => {
        console.error('Erro ao carregar dados de dispositivos:', error);
        return of(this.getMockDeviceStatusData());
      }),
    );
  }

  getRecentActivity(): Observable<ActivityItem[]> {
    return forkJoin({
      checkIns: this.statusManageService.getCheckins(),
      deviceRequests: this.statusManageService.getDeviceRequests(),
      guards: this.statusManageService.getGuards(),
      positions: this.statusManageService.getPositions(),
    }).pipe(
      map(({ checkIns, deviceRequests, guards, positions }) => {
        return this.generateRecentActivity(checkIns, deviceRequests, guards, positions);
      }),
    );
  }

  private generateRecentActivity(
    checkIns: any[],
    deviceRequests: any[],
    guards: any[],
    positions: any[],
  ): ActivityItem[] {
    const activities: ActivityItem[] = [];

    // Adicionar check-ins recentes
    checkIns.slice(-5).forEach((checkin) => {
      activities.push({
        id: `checkin-${checkin.id}`,
        type: 'checkin',
        description: `Check-in realizado por ${checkin.guard?.first_name || 'Vigilante'}`,
        timestamp: new Date(checkin.created_at),
        icon: 'clock',
        color: 'text-green-600',
      });
    });

    // Adicionar solicitações de dispositivos recentes
    deviceRequests.slice(-3).forEach((request) => {
      activities.push({
        id: `request-${request.id}`,
        type: 'device_request',
        description: `Nova solicitação de dispositivo: ${request.device_name}`,
        timestamp: new Date(request.requested_at),
        icon: 'smartphone',
        color: 'text-blue-600',
      });
    });

    // Ordenar por timestamp (mais recente primeiro)
    return activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 10);
  }

  refreshStats(): Observable<DashboardStats> {
    return this.statusManageService.forceSync().pipe(switchMap(() => this.getDashboardStats()));
  }

  private getLast7Days(): string[] {
    const days: string[] = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }

    return days;
  }

  private groupCheckInsByDay(checkIns: any[], days: string[]): { [key: string]: number } {
    const grouped: { [key: string]: number } = {};

    days.forEach((day) => {
      grouped[day] = 0;
    });

    checkIns.forEach((checkin) => {
      const checkinDate = new Date(checkin.created_at).toISOString().split('T')[0];
      if (grouped.hasOwnProperty(checkinDate)) {
        grouped[checkinDate]++;
      }
    });

    return grouped;
  }

  private formatDateLabel(date: string): string {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });
  }

  private countDevicesByStatus(devices: any[]): { [key: string]: number } {
    const statusCounts: { [key: string]: number } = {
      activo: 0,
      inactivo: 0,
      Manutenção: 0,
    };

    devices.forEach((device) => {
      if (device.is_active) {
        statusCounts['activo']++;
      } else {
        const status = device.status || 'inactivo';
        if (statusCounts.hasOwnProperty(status)) {
          statusCounts[status]++;
        } else {
          statusCounts['inactivo']++;
        }
      }
    });

    return statusCounts;
  }

  private getMockCheckInsData(): ChartData {
    const last7Days = this.getLast7Days();
    const mockData = [12, 19, 15, 25, 22, 18, 24];

    return {
      labels: last7Days.map((date) => this.formatDateLabel(date)),
      datasets: [
        {
          label: 'Check-Presence',
          data: mockData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }

  private getMockDeviceStatusData(): ChartData {
    return {
      labels: ['activo', 'inactivo', 'Manutenção'],
      datasets: [
        {
          label: 'Dispositivos',
          data: [45, 8, 3],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(249, 115, 22, 0.8)',
          ],
          borderColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)', 'rgb(249, 115, 22)'],
          borderWidth: 2,
        },
      ],
    };
  }
}
